import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, User, Mail, Briefcase, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Automotive",
  "Banking/Finance",
  "Food & Beverage",
  "Healthcare",
  "Insurance",
  "Retail",
  "Restaurant",
  "Real Estate",
  "Sports/Fitness",
  "Technology",
  "Other"
];

export const SponsorSignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("code") || "";
  
  const [formData, setFormData] = useState({
    invite_code: inviteCode,
    name: "",
    company: "",
    email: "",
    business_type: "",
    username: "",
    password: ""
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingCode, setValidatingCode] = useState(true);
  const [codeValid, setCodeValid] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Validate invite code
  useEffect(() => {
    const validateCode = async () => {
      if (!inviteCode) {
        setValidatingCode(false);
        setError("Invalid invite link. Please request a new invitation.");
        return;
      }

      try {
        await axios.get(`${API}/sponsors/invite/${inviteCode}/validate`);
        setCodeValid(true);
        setFormData(prev => ({ ...prev, invite_code: inviteCode }));
      } catch (err) {
        setError("This invite link is invalid or has expired. Please request a new invitation.");
      } finally {
        setValidatingCode(false);
      }
    };
    validateCode();
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/sponsors/signup`, formData);
      
      // Store token
      localStorage.setItem("sponsor_token", response.data.token);
      localStorage.setItem("sponsor_id", response.data.sponsor_id);
      
      setSuccess(true);
      
      // Redirect to portal after 2 seconds
      setTimeout(() => {
        navigate("/sponsor/portal");
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  if (validatingCode) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!codeValid) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="font-secondary text-2xl font-bold text-white mb-4">Invalid Invite</h1>
          <p className="text-zinc-400 mb-8">{error}</p>
          <a href="/" className="text-zinc-500 hover:text-white">← Back to Website</a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md"
        >
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h1 className="font-secondary text-2xl font-bold text-white mb-4">Account Created!</h1>
          <p className="text-zinc-400 mb-8">Redirecting you to the Sponsor Portal...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_363f79c5-085c-4461-8ddb-f89341e71c50/artifacts/832gaaj4_ASN%20Short%20Wordmark.png" 
            alt="Antler Sports Network"
            className="h-12 w-auto mx-auto mb-6"
          />
          <h1 className="font-secondary text-3xl font-bold uppercase tracking-tight text-white mb-2">
            Sponsor Portal
          </h1>
          <p className="text-zinc-400">Create your account to access the sponsor portal</p>
        </div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="bg-zinc-900/50 border border-white/10 p-8 space-y-6"
        >
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-500 text-sm">{error}</span>
            </div>
          )}

          {/* Personal Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                <User className="w-4 h-4 inline mr-1" /> Your Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full bg-black border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-white/30"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" /> Company Name *
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
                className="w-full bg-black border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-white/30"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                <Mail className="w-4 h-4 inline mr-1" /> Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full bg-black border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-white/30"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                <Briefcase className="w-4 h-4 inline mr-1" /> Business Type *
              </label>
              <select
                value={formData.business_type}
                onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                required
                className="w-full bg-black border border-white/10 text-white px-4 py-3 text-sm"
              >
                <option value="">Select...</option>
                {BUSINESS_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          <hr className="border-white/10" />

          {/* Account Credentials */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
              Username *
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
              required
              placeholder="Choose a username"
              className="w-full bg-black border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-white/30"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                <Lock className="w-4 h-4 inline mr-1" /> Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder="Min 8 characters"
                  className="w-full bg-black border border-white/10 text-white px-4 py-3 pr-10 text-sm focus:outline-none focus:border-white/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                Confirm Password *
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full bg-black border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-white/30"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm py-4 h-auto rounded-none"
          >
            {loading ? "Creating Account..." : "Create Sponsor Account"}
          </Button>

          <p className="text-center text-zinc-500 text-xs">
            Already have an account?{" "}
            <a href="/sponsor/login" className="text-white hover:underline">Sign In</a>
          </p>
        </motion.form>
      </div>
    </div>
  );
};

export default SponsorSignupPage;
